/**
 * `useProfileExtraction` — dashboard hook for the opt-in site-content
 * analysis pipeline (ADR-0040 Slice 1 Layer 3).
 *
 * Wraps the `ProfileExtractionAPI` bag with the polling + lifecycle
 * shape the `VerifyStep` and `SkillsStep` need:
 *
 *   - `start()` — fires `POST /v1/sites/:site_id/profile-extraction`
 *     (the bare path; the server route handler returns the 202 ack
 *     `{ profile_extraction_id, status: 'running' }`), then
 *     transitions into the polling loop until the result lands
 *     (`status === 'complete' | 'failed'`).
 *   - `getLatest` is called on mount so a merchant returning to
 *     SkillsStep after VerifyStep kicked off the run sees the
 *     suggestion as soon as it is complete.
 *   - Polling is at 2s intervals with a 30s bound — past the bound the
 *     hook flips to a synthetic `'failed'` state with reason `'timeout'`
 *     so the view layer renders the soft no-suggestion panel rather
 *     than spinning indefinitely.
 *   - Every error path collapses to a synthetic `'failed'` state. The
 *     hook never throws to the caller; the view layer renders the same
 *     "couldn't analyze your site" message regardless of root cause.
 *   - `isAvailable` flips to `false` when `getLatest` (on mount) or
 *     `start()` (on click) returns null — that signals the server's
 *     feature flag is off (or the site is unknown under tenancy) and
 *     the view layer hides the opt-in entirely.
 *   - The hook's "no extraction yet" state is the bag returning null
 *     on the on-mount read — NOT a server-side `'skipped'` status (no
 *     such status exists on the wire; the server's status enum is the
 *     three-element `'running' | 'complete' | 'failed'`).
 *
 * Cleanup: the polling interval is held in a ref and cleared on
 * unmount AND on every state transition that ends polling, so a
 * site-switch mid-run does not leak a setInterval into the next mount.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { ProfileExtractionResult, SiteId } from '@sill/types';

import { useApi } from '../api/api-context.js';
import { ApiError } from '../api/http-client.js';

/** Hook-facing extraction state — a superset of the wire
 *  `ProfileExtractionStatus` plus an idle floor for "merchant has not
 *  clicked / no run on the server yet". The hook maps the wire
 *  `'complete'` to the UI `'complete'`; both VerifyStep and SkillsStep
 *  key off `'complete'` to render the suggestion surface. */
export type ProfileExtractionUiStatus =
  | 'idle'
  | 'running'
  | 'complete'
  | 'failed';

export interface UseProfileExtractionState {
  /** Lifecycle for the panel + opt-in affordance. */
  status: ProfileExtractionUiStatus;
  /** Populated when `status === 'complete'`. */
  suggestedSkills: ProfileExtractionResult['suggested_skills'] | undefined;
  /** Opaque ops-correlation token when `status === 'failed'`. The view
   *  layer never renders this — it shows a single soft message
   *  regardless of root cause (ADR-0040 §6.6). */
  failureReason: string | undefined;
  /**
   * Whether the feature is offered at all on this environment.
   * `true` until `getLatest` / `start` returns null (the server's
   * feature flag is off, OR the site is unknown under tenancy). The
   * opt-in affordance is gated on this.
   */
  isAvailable: boolean;
  /** Kick off an extraction run. Idempotent if a run is already
   *  pending — callers can safely double-click. */
  start: () => Promise<void>;
}

const POLL_INTERVAL_MS = 2_000;
const POLL_MAX_MS = 30_000;

export function useProfileExtraction(
  site_id: SiteId | null | undefined,
): UseProfileExtractionState {
  const api = useApi();
  const [status, setStatus] = useState<ProfileExtractionUiStatus>('idle');
  const [suggestedSkills, setSuggestedSkills] = useState<
    ProfileExtractionResult['suggested_skills'] | undefined
  >(undefined);
  const [failureReason, setFailureReason] = useState<string | undefined>(undefined);
  const [isAvailable, setIsAvailable] = useState<boolean>(true);

  /** Polling timer handle. Held in a ref so the cleanup callback
   *  (and every transition that ends polling) can clear it without
   *  threading the value through state. */
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Wall-clock the current poll loop started at — for the 30s bound. */
  const pollStartedAt = useRef<number>(0);
  /** Mounted flag — guard async state updates after unmount. */
  const mounted = useRef<boolean>(true);

  const clearPoll = useCallback((): void => {
    if (pollTimer.current !== null) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const applyResult = useCallback(
    (result: ProfileExtractionResult): void => {
      if (!mounted.current) return;
      if (result.status === 'complete') {
        clearPoll();
        setStatus('complete');
        setSuggestedSkills(result.suggested_skills ?? []);
        setFailureReason(undefined);
        return;
      }
      if (result.status === 'failed') {
        clearPoll();
        setStatus('failed');
        setSuggestedSkills(undefined);
        setFailureReason(result.failure_reason ?? 'unknown');
        return;
      }
      // running — keep polling. (The wire enum is closed at
      // 'running' | 'complete' | 'failed' per ADR-0040 §8; this branch
      // is the only remaining case.)
      setStatus('running');
    },
    [clearPoll],
  );

  const markFailed = useCallback(
    (reason: string): void => {
      if (!mounted.current) return;
      clearPoll();
      setStatus('failed');
      setSuggestedSkills(undefined);
      setFailureReason(reason);
    },
    [clearPoll],
  );

  const beginPolling = useCallback((): void => {
    if (site_id === null || site_id === undefined) return;
    clearPoll();
    pollStartedAt.current = Date.now();
    const tick = async (): Promise<void> => {
      if (!mounted.current || site_id === null || site_id === undefined) {
        clearPoll();
        return;
      }
      // Bound the poll: past 30s flip to a synthetic timeout failure
      // and stop polling. Matches the server-side orphan-sweep window
      // (ADR-0040 §5.3) so the dashboard and server agree on the
      // give-up boundary.
      if (Date.now() - pollStartedAt.current > POLL_MAX_MS) {
        markFailed('timeout');
        return;
      }
      try {
        const result = await api.profile_extraction.getLatest(site_id);
        if (result === null) {
          // 404 mid-poll — collapse to "no panel" and stop. Don't
          // surface as failed; the merchant either canceled or the
          // feature flag flipped.
          clearPoll();
          if (mounted.current) {
            setStatus('idle');
            setIsAvailable(false);
          }
          return;
        }
        applyResult(result);
      } catch (err) {
        // Any error → fail-soft. The view layer renders the soft
        // no-suggestion panel; the merchant proceeds with defaults.
        markFailed(
          err instanceof ApiError ? err.kind : 'internal_error',
        );
      }
    };
    pollTimer.current = setInterval(() => {
      void tick();
    }, POLL_INTERVAL_MS);
    // Fire one tick immediately so a fast-completing run doesn't wait
    // the full 2s for its first observation.
    void tick();
  }, [api, applyResult, clearPoll, markFailed, site_id]);

  const start = useCallback(async (): Promise<void> => {
    if (site_id === null || site_id === undefined) return;
    setStatus('running');
    setSuggestedSkills(undefined);
    setFailureReason(undefined);
    try {
      const result = await api.profile_extraction.start(site_id);
      if (result === null) {
        // Feature flag off (or site unknown under tenancy) — hide the
        // opt-in surface entirely.
        clearPoll();
        if (mounted.current) {
          setStatus('idle');
          setIsAvailable(false);
        }
        return;
      }
      applyResult(result);
      if (result.status === 'running') {
        beginPolling();
      }
    } catch (err) {
      // Rate-limited / 5xx / anything → fail-soft. The view layer
      // renders the soft no-suggestion message; nothing blocks the
      // merchant.
      markFailed(err instanceof ApiError ? err.kind : 'internal_error');
    }
  }, [api, applyResult, beginPolling, clearPoll, markFailed, site_id]);

  // On mount (or when the active site changes), check for an existing
  // run on this site. If one is already complete the SkillsStep can
  // surface the panel immediately; if one is running the hook joins
  // the existing poll loop instead of kicking off a fresh run.
  useEffect(() => {
    mounted.current = true;
    if (site_id === null || site_id === undefined) {
      return (): void => {
        mounted.current = false;
      };
    }
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const result = await api.profile_extraction.getLatest(site_id);
        if (cancelled || !mounted.current) return;
        if (result === null) {
          // No run yet OR feature off — the dashboard can't tell the
          // two apart from a 404 / `{ extraction: null }`, and treats
          // both the same in the SkillsStep panel (don't render). The
          // opt-in affordance in VerifyStep checks `isAvailable` — we
          // do NOT flip it false here because a `{ extraction: null }`
          // on a fresh site is the normal first-visit case and the
          // merchant SHOULD see the opt-in. The conservative default
          // is "show the opt-in" until we have evidence it's off,
          // which is `start()` returning null.
          return;
        }
        applyResult(result);
        if (result.status === 'running') {
          beginPolling();
        }
      } catch (err) {
        // On-mount errors are intentionally NON-blocking — we don't
        // want a transient network blip to hide the opt-in. But don't
        // swallow totally: the http client already fired
        // `on_unauthorized` for 401s; we additionally surface the
        // failure CLASS (kind only — never payload, the bag is
        // typed and the error class is bounded) to console.debug so
        // a developer hitting an auth-vs-network blip can tell them
        // apart. The fail-soft behavior is preserved: the merchant
        // clicking Analyze will surface the real state.
        if (typeof console !== 'undefined' && typeof console.debug === 'function') {
          const kind = err instanceof ApiError ? err.kind : 'unknown';
          console.debug(
            '[useProfileExtraction] on-mount getLatest failed; opt-in left visible',
            { kind },
          );
        }
      }
    })();
    return (): void => {
      cancelled = true;
      mounted.current = false;
      clearPoll();
    };
  }, [api, applyResult, beginPolling, clearPoll, site_id]);

  return {
    status,
    suggestedSkills,
    failureReason,
    isAvailable,
    start,
  };
}
